import SectionCard from '@Guidelines/Component/SectionCard';
import type SubjectTypeDefinitionFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/SubjectTypeDefinitionFormData';
import AppearancePreview from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Appearance/AppearancePreview';
import ColorSwatch from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Appearance/ColorSwatch';
import IconOption from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Appearance/IconOption';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { useSelector } from '@verclam/synapse';
import { Dropdown } from '@verclam/ui-kit';

type Props = {
	readonly formData: SubjectTypeDefinitionFormData;
	readonly onChange: (
		update: (formData: SubjectTypeDefinitionFormData) => SubjectTypeDefinitionFormData,
	) => void;
	readonly isReadOnly: boolean;
};

/** The icon and the color that distinguish the type, chosen from the server lists, with a preview. */
const AppearanceSection: React.FC<Props> = ({ formData, onChange, isReadOnly }) => {
	const icons = useSelector((state) => state.subjectTypeDefinitionsStore.icons);
	const colors = useSelector((state) => state.subjectTypeDefinitionsStore.colors);
	const iconMessage = formData.getIconServerMessage();
	const colorMessage = formData.getColorServerMessage();

	const handleIconChange = React.useCallback(
		(value: string | null) => {
			if (value !== null) {
				onChange((current) => current.setIcon(value));
			}
		},
		[onChange],
	);

	const handleColorChange = React.useCallback(
		(value: string | null) => {
			if (value !== null) {
				onChange((current) => current.setColor(value));
			}
		},
		[onChange],
	);

	const renderIcon = React.useCallback((option: unknown) => {
		if (!(option instanceof LabeledValueView)) {
			return null;
		}

		return <IconOption icon={option.value} label={option.label} />;
	}, []);

	const renderColor = React.useCallback((option: unknown) => {
		if (!(option instanceof LabeledValueView)) {
			return null;
		}

		return <ColorSwatch color={option.value} label={option.label} />;
	}, []);

	return (
		<SectionCard
			title="Apparence"
			description="L'icône et la couleur distinguent ce type sur l'accueil et dans les menus."
		>
			<Grid columns={12} columnGap={1} rowGap={1}>
				<GridItem columnSize={{ xs: 12, md: 6 }}>
					<Dropdown<string>
						name="icon"
						label="Icône"
						required
						disabled={isReadOnly}
						options={icons ?? []}
						optionLabel="label"
						optionValue="value"
						value={formData.icon}
						onChange={handleIconChange}
						itemTemplate={renderIcon}
						valueTemplate={renderIcon}
						errorMessage={iconMessage ?? undefined}
						isFormFieldValid={iconMessage === null}
					/>
				</GridItem>
				<GridItem columnSize={{ xs: 12, md: 6 }}>
					<Dropdown<string>
						name="color"
						label="Couleur"
						required
						disabled={isReadOnly}
						options={colors ?? []}
						optionLabel="label"
						optionValue="value"
						value={formData.color}
						onChange={handleColorChange}
						itemTemplate={renderColor}
						valueTemplate={renderColor}
						errorMessage={colorMessage ?? undefined}
						isFormFieldValid={colorMessage === null}
					/>
				</GridItem>
				<GridItem>
					<AppearancePreview icon={formData.icon} color={formData.color} name={formData.plural} />
				</GridItem>
			</Grid>
		</SectionCard>
	);
};

export default AppearanceSection;
